/**
 * Split a paragraph into plain + marked segments so existing annotations render
 * as inline `<mark>`s. Pure — no DOM. Overlapping marks are resolved by keeping
 * the earliest-starting, longest span (a later mark that overlaps is skipped;
 * it still shows as a gutter chip). Marks whose `exact` is not found in the
 * paragraph are ignored here (they belong to another paragraph).
 */

import type { AnnotationKind, Severity } from "@platform/rlhf-core"

/** A mark to place: the quote to find plus the display facets. */
export interface MarkSpec {
  id: string
  exact: string
  kind: AnnotationKind
  severity: Severity
}

/** One rendered piece of a paragraph — plain text, or text carrying a mark. */
export interface Segment {
  text: string
  mark?: MarkSpec
}

interface Placed {
  start: number
  end: number
  mark: MarkSpec
}

/** Segment `text` around the given marks (first occurrence of each `exact`). */
export function segmentParagraph(text: string, marks: readonly MarkSpec[]): Segment[] {
  const placed: Placed[] = []
  for (const mark of marks) {
    if (!mark.exact) continue
    const start = text.indexOf(mark.exact)
    if (start === -1) continue
    placed.push({ start, end: start + mark.exact.length, mark })
  }
  placed.sort((a, b) => a.start - b.start || b.end - a.end)

  const segments: Segment[] = []
  let cursor = 0
  for (const { start, end, mark } of placed) {
    if (start < cursor) continue // overlaps an already-placed mark → skip (gutter chip still shows it)
    if (start > cursor) segments.push({ text: text.slice(cursor, start) })
    segments.push({ text: text.slice(start, end), mark })
    cursor = end
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor) })
  return segments
}
